import { Inject, Injectable, PLATFORM_ID } from '@angular/core'
import { HttpClient, HttpErrorResponse, HttpHeaders } from '@angular/common/http'
import { BehaviorSubject, Observable } from 'rxjs'
import {
  AuthCredentials,
} from '../../shared/models/credentials'
import { LoginResponse, LogoutResponse, mockFailureLoginResponse, mockSuccessLoginResponse, VerifyUserRoleResponse } from '../../shared/api/login'
import { map } from 'rxjs/operators'
import { USER_LOGIN_GROUP } from '../../account-api'
import { ACCOUNT_AUTH_GROUP } from '../../account-api'
import { ModalStateService } from 'src/app/core/services/modal-state.service'
import { UserProps } from './additional/UserProps'
import { isPlatformBrowser } from '@angular/common'
import { SharedDataService } from 'src/app/core/services/shared-data.service'

@Injectable({
  providedIn: 'root',
})
export class LoginService {

  private _isLoggedIn: BehaviorSubject<boolean> = new BehaviorSubject(false)
  public isMocksEnabled = false
  public showExpiredModal = false
  public sessionTimeout = null
  private sessionTimer = null

  showHideMenu: BehaviorSubject<any> = new BehaviorSubject([])

  constructor(
    private http: HttpClient,
    private modalStateService: ModalStateService,
    public sharedDataService: SharedDataService,
    @Inject(PLATFORM_ID) private platformId: object,
  ) {
    if (isPlatformBrowser(this.platformId)) {
      this.createTimer()
    }




  }



  savedLoginResponse: LoginResponse
  authCreds: AuthCredentials


  // Auth management and else states

  get isLoggedIn(): Observable<boolean>{
    return this._isLoggedIn.asObservable()
  }

  get isLoggedValue(): boolean{
    return this._isLoggedIn.value
  }

  public saveUserProps(res: LoginResponse): UserProps {
    const props: UserProps = {
      userSessionToken: res.resultMap.accessToken,
      userRefreshToken: res.resultMap.refreshToken,
      userRefreshTokenExpiresOn: Date.now() + 900000,
      userTokenExpiresOn: res.resultMap.expiresOn,
      userTokenLife: res.resultMap.tokenLife,
    }
    this.modalStateService.pushToLocalStorageProps(props)
    return props
  }


  
 public requestLoanStatus(): Observable<any> {
  return this.http.get(USER_LOGIN_GROUP.GET_LOAN_STATUS) as any
}

  // duck typing
  public getUserProps = (): UserProps =>
    this.modalStateService.getFromLocalStorageClass(new UserProps())


  /**
   * @deprecated
   * make {@link LoginService.postLogin } with saved auth creds
   */
  public refreshLogin(): Observable<LoginResponse> {
    const userCreds = null
    return this.postLogin(userCreds, true)
    .pipe(map(res => {
      const props = this.saveUserProps(res)
      return res
    }))
  }


  /**
   * clear local storage
   * proceed to login page
   */
   public clearAndProceedLogin() {
    this.modalStateService.removeFromLocalStorage(new UserProps())
    this.modalStateService.removeTokensAndCreds()
    this._isLoggedIn.next(false)
  }

  private proceedExpiredToken() {
    this.clearAndProceedLogin()
  }

  /**
   * check props (expiresOn and token)
   * and pass _isLoggedIn result of checking
   */
  public authCheck(): void {
    const isLogout = this.modalStateService.getFromLocalStorage('isLogout')
    if (isLogout) {
      this._isLoggedIn.next(false)
      return
    }
    const props = this.getUserProps()
    if (props.userRefreshTokenExpiresOn && props.userSessionToken){
      const refreshExpiresOn = props.userRefreshTokenExpiresOn
      // still not expired
      if (refreshExpiresOn > Date.now()){
        this._isLoggedIn.next(true)
        this.getShowHideMainMenu()
        return
      }
      // expired
      this.proceedExpiredToken()
    }
  }



  public logout(): Observable<LogoutResponse> {
    const props = this.getUserProps()
    return this.requestPostLogout({accessToken: props.userSessionToken})
    .pipe(map(res => {
      if (res.status === 'OK'){
        this.modalStateService.pushToLocalStorage('isLogout', true)
        this.sharedDataService.dispAmortization$.next(false)
        this.clearAndProceedLogin()
      }
      return res
    }))
  }


   public getShowHideMainMenu(){
    this.requestLoanStatus() .subscribe(res => {
      if (res?.data?.tabs) {
        this.showHideMenu.next(res?.data?.tabs)
      }
     }, err => {
       console.log(err)
    })
  }

  // requests
  public postLogin(
    body: AuthCredentials,
    refresh?: boolean,
  ): Observable<LoginResponse> {
    this.authCreds = body
    return this.requestPostLogin(body, refresh)
    .pipe(map(res => {
      this.setRunTimerByTimeout()
      this.savedLoginResponse = res
      // if (res.status === 'OK'){
      //   this._isLoggedIn.next(true)
      // }
      return this.savedLoginResponse
    }))
  }

  /**
   *
   * @returns true if success, false if failure
   */
  public getVerifyResult(): Observable<boolean> {
    return this.requestGetVerifyUserRole().pipe(map(res => {
      if (res?.resultMap?.userAccessToApp === true) {
        return true
      }
      return false
    }))
  }

  public sendNotification(): Observable<any>{
    return this.http.post(USER_LOGIN_GROUP.POST_USER_LOGIN_NOTIFICATION, {channelId: 'WEBPQ'})
  }

  public accountActivation(data): Observable<any>{
    return this.http.post(ACCOUNT_AUTH_GROUP.ACCOUNT_ACTIVATION, data)
  }

  public sendActivationNotification(data): Observable<any>{
    return this.http.post(ACCOUNT_AUTH_GROUP.SEND_ACTIVATION_NOTIFICATION, data)
  }
  
  private requestGetVerifyUserRole(): Observable<VerifyUserRoleResponse> {
      return this.http.get(USER_LOGIN_GROUP.GET_VERIFY_USER_ROLE_HAS_ACCESS) as any
      // return this.http.get(USER_LOGIN_GROUP.GET_VERIFY_USER_ROLE_HAS_ACCESS) as any
  }


  private requestPostLogin(
    body: AuthCredentials,
    refresh?: boolean,
  ): Observable<LoginResponse> {
    this.isMocksEnabled = false
    let headers = new HttpHeaders()
    if (refresh) {
      const props = this.modalStateService.getFromLocalStorageClass(new UserProps())
      headers = headers.set('Authorization', `Bearer ${props.userRefreshToken}`)
    }
    return this.http.post(ACCOUNT_AUTH_GROUP.POST_USER_LOGIN, body, { headers }) as any
  }


  private requestPostLogout(
    body: {accessToken: string},
  ): Observable<LogoutResponse> {
    return this.http.post(USER_LOGIN_GROUP.POST_USER_LOGOUT, body) as any
  }

  createTimer(){
    const minutes = 14
    if (this.sessionTimer){
      clearTimeout(this.sessionTimer)
    }
    this.sessionTimer = this.generateTokenOnTimeout(minutes)
  }

  generateTokenOnTimeout(minutes: number){
    return setTimeout(() => {
    const creds: AuthCredentials = this.modalStateService.getFromLocalStorage('userCreds', true)
    if (!creds){
      return
    }
    this.postLogin(creds, true).subscribe(res => {
      this.saveUserProps(res)
      this.createTimer()
     }, err => {
      this.logout().subscribe(res => {
      }, error => {
      })
    })
  }, minutes * 60 * 1000)
  }

  setRunTimerByTimeout(minutes?) {
    if (!minutes) {
      minutes = 13
    }
    if (this.sessionTimeout) {
      clearTimeout(this.sessionTimeout)
    }
    this.sessionTimeout = this.rmDataByTimeout(minutes)
  }


  rmDataByTimeout(minutes: number) {
    return setTimeout(() => {
      this.showExpiredModal = true
    }, minutes * 60 * 1000)
  }
}

